export const timeWindowUnits = ['weeks', 'days', 'hours', 'minutes', 'seconds'];
export type TimeWindowUnit = (typeof timeWindowUnits)[number];

export const timespanInSeconds: { [unit in TimeWindowUnit]: number } = {
  seconds: 1,
  minutes: 60,
  hours: 60 * 60,
  days: 60 * 60 * 24,
  weeks: 60 * 60 * 24 * 7,
};

export const getWindowAsSeconds = (window: string): number =>
  Number(window.slice(0, -1));

export const parseDuration = (duration: string): number => {
  const match = duration.match(/^([\d.]+)s$/);
  return match ? parseFloat(match[1]) : 0;
};

const formatDurationUnit = (
  value: number,
  divisor: number,
  unit: string,
): string => {
  const hasRemainder = value % divisor !== 0;
  if (hasRemainder) {
    const formatted = (value / divisor).toFixed(1);
    if (formatted.endsWith('.0')) {
      const whole = parseInt(formatted, 10);
      return `${whole} ${unit}${whole !== 1 ? 's' : ''}`;
    }
    return `${formatted} ${unit}s`;
  }
  const whole = value / divisor;
  return `${whole} ${unit}${whole !== 1 ? 's' : ''}`;
};

export const formatDuration = (seconds: number): string => {
  if (seconds < timespanInSeconds.minutes) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
  if (seconds < timespanInSeconds.hours) {
    return formatDurationUnit(seconds, timespanInSeconds.minutes, 'minute');
  }
  if (seconds < timespanInSeconds.days) {
    return formatDurationUnit(seconds, timespanInSeconds.hours, 'hour');
  }
  return formatDurationUnit(seconds, timespanInSeconds.days, 'day');
};

export const getClosestUnit = (seconds: number): TimeWindowUnit => {
  for (let i = 0; i < timeWindowUnits.length; i++) {
    const unit = timeWindowUnits[i];
    const unitInSeconds = timespanInSeconds[unit];
    if (seconds >= unitInSeconds && seconds % unitInSeconds === 0) {
      return unit;
    }
  }
  return 'seconds';
};

export const createWindow = (amount: number, unit: TimeWindowUnit): string =>
  `${Number(amount) * timespanInSeconds[unit]}s`;

export const getWindowForUnit = (window: string, unit: TimeWindowUnit) =>
  getWindowAsSeconds(window) / timespanInSeconds[unit];

export const parseWindow = (timeWindow: string) => {
  const unit = getClosestUnit(getWindowAsSeconds(timeWindow));
  const value = getWindowForUnit(timeWindow, unit);
  if (!timeWindow || !isFinite(value)) {
    return 'None';
  }
  return `${value} ${value === 1 ? unit.slice(0, -1) : unit}`;
};

export const sortTimewindows = (a: string, b: string) =>
  getWindowAsSeconds(a) - getWindowAsSeconds(b);

export const getSecondsForAmountAndUnit = (
  amount: number,
  unit: TimeWindowUnit,
): number => getWindowAsSeconds(createWindow(amount, unit));

export const formatSecondsAsWindowLabel = (seconds: number): string =>
  parseWindow(`${seconds}s`);

export const parseSecondsToAmountAndUnitCoarse = (
  seconds: number,
): {
  amount: number;
  unit: Exclude<TimeWindowUnit, 'seconds'>;
} => {
  const twUnit = getClosestUnit(seconds);
  if (twUnit === 'seconds') {
    return {
      amount: Math.max(1, Math.round(seconds / 60)),
      unit: 'minutes',
    };
  }
  return {
    amount: getWindowForUnit(`${seconds}s`, twUnit),
    unit: twUnit,
  };
};
